import { getElement } from '../Utility/Dom'
import { IS_ACTIVE, IS_FINAL, IS_REMOVING } from '../Utility/State'
import { getScrollPosition } from '../Utility/Viewport'

class TopBar {
  private readonly element: HTMLElement | null
  private readonly finalTransitionDuration: number = 300
  private isRemoving: boolean = false
  private isUserScrolling: boolean
  private lastPositionY: number
  private positionY: number
  private readonly removeTransitionDuration: number = 150

  private scrollDirection: number
  private readonly scrollDirectionDown: number = 1
  private readonly scrollDirectionUp: number = 0
  private readonly scrollThrottleInterval: number = 250
  private readonly scrollUpDelayInterval: number = 400
  private scrollUpTimeout: null | number
  private readonly triggerOffset: number = 100

  constructor() {
    this.element = getElement('top-bar')
    this.isUserScrolling = false
    this.positionY = getScrollPosition()
    this.lastPositionY = 0
    this.scrollDirection = this.scrollDirectionDown
    this.scrollUpTimeout = null
  }

  public init(): void {
    if (this.element === null) return

    setInterval((): void => {
      if (this.isUserScrolling === true) {
        this.isUserScrolling = false
        this.updateScrollPosition()
        this.updateScrollDirection()
        this.updateLastScrollPosition()
        this.update()
      }
    }, this.scrollThrottleInterval)

    this.update()
    this.addEvents()
  }

  private addClasses(): void {
    if (this.isRemoving) return

    if (this.element?.classList.contains(IS_ACTIVE)) return

    this.element?.classList.add(IS_ACTIVE)

    requestAnimationFrame((): void => {
      setTimeout((): void => {
        this.element?.classList.add(IS_FINAL)
      }, this.finalTransitionDuration)
    })
  }

  private addEvents(): void {
    window.addEventListener('scroll', (): void => {
      this.isUserScrolling = true
    }, {passive: true})

    window.addEventListener('resize', (): void => {
      this.isUserScrolling = true
    }, {passive: true})
  }

  private removeClasses(animated: boolean): void {
    if (!this.element?.classList.contains(IS_ACTIVE)) return

    if (!animated) {
      this.element?.classList.remove(IS_ACTIVE, IS_FINAL, IS_REMOVING)
      this.isRemoving = false
      return
    }

    this.isRemoving = true

    this.element?.classList.add(IS_REMOVING)
    void this.element?.offsetHeight

    requestAnimationFrame((): void => {
      this.element?.classList.remove(IS_FINAL)

      setTimeout((): void => {
        this.element?.classList.remove(IS_ACTIVE, IS_REMOVING)
        this.isRemoving = false
      }, this.removeTransitionDuration)
    })
  }

  private update(): void {
    if (this.scrollDirection === this.scrollDirectionDown) {
      this.removeClasses(true)
      return
    }

    if (
      this.positionY < this.triggerOffset + (this.element?.offsetHeight || 0)
    ) {
      this.removeClasses(false)
      return
    }

    this.addClasses()
  }

  private updateLastScrollPosition(): void {
    this.lastPositionY = this.positionY <= 0 ? 0 : this.positionY
  }

  private updateScrollDirection(): void {
    if (this.positionY > this.lastPositionY) {
      if (this.scrollUpTimeout) {
        clearTimeout(this.scrollUpTimeout)
      }
      this.scrollDirection = this.scrollDirectionDown
    } else {
      if (this.scrollUpTimeout) {
        clearTimeout(this.scrollUpTimeout)
      }
      this.scrollUpTimeout = setTimeout((): void => {
        this.scrollDirection = this.scrollDirectionUp
      }, this.scrollUpDelayInterval)
    }
  }

  private updateScrollPosition(): void {
    this.positionY = getScrollPosition()
  }
}

const topBar: TopBar = new TopBar()
topBar.init()
